import { useEffect, useState, type ReactNode } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { Meter, StaffActivity } from "@backstory/protocol";
import { AutoLoadMore, SearchSelect, fmtNum, fmtUsd } from "../../components/ui";
import { ErrorState } from "../../components/States";
import { formatDateTime, formatRelative } from "../../lib/format";
import { ROLE_LABELS } from "../../auth/roles";
import { staffApi, staffKeys, type ActivityFilters } from "./api";

// ---------------------------------------------------------------------------
// Words
// ---------------------------------------------------------------------------

/** A role in the words the product uses; unknown ones pass through. */
export function roleWord(role: string): string {
  return (ROLE_LABELS as Record<string, string>)[role] ?? role;
}

export const PLAN_LABELS: Record<string, string> = { free: "Free", team: "Team", business: "Business", enterprise: "Enterprise" };
export const PLAN_ORDER = ["free", "team", "business", "enterprise"] as const;

export function planLabel(plan: string): string {
  return PLAN_LABELS[plan] ?? plan;
}

export const BILLING_STATUS: Record<string, { label: string; tone: string }> = {
  active: { label: "Active", tone: "ok" },
  trialing: { label: "Trial", tone: "info" },
  past_due: { label: "Past due", tone: "warn" },
  unpaid: { label: "Unpaid", tone: "danger" },
  canceled: { label: "Canceled", tone: "neutral" },
  none: { label: "No subscription", tone: "neutral" },
};

export function statusOf(status: string): { label: string; tone: string } {
  return BILLING_STATUS[status] ?? { label: status.replaceAll("_", " "), tone: "neutral" };
}

export function PlanBadge({ plan }: { plan: string }) {
  const tone = plan === "enterprise" ? "ai" : plan === "business" ? "info" : plan === "team" ? "ok" : "neutral";
  return <span className={`badge ${tone}`}>{planLabel(plan)}</span>;
}

export function BillingStatusBadge({ status }: { status: string }) {
  const s = statusOf(status);
  return <span className={`badge ${s.tone}`}>{s.label}</span>;
}

/** Meters in display order, with the unit they count. */
export const METERS: Array<{ id: Meter; label: string; unit: string; usd?: boolean; perMillion?: boolean }> = [
  { id: "stored_sessions", label: "Stored sessions", unit: "sessions" },
  { id: "custom_events", label: "Custom events", unit: "events", perMillion: true },
  { id: "ai_credits_usd", label: "AI credits", unit: "", usd: true },
  { id: "deep_analyses", label: "Deep analyses", unit: "analyses" },
  { id: "canvas_minutes", label: "Visual recording minutes", unit: "min" },
  { id: "live_minutes", label: "Live Assist minutes", unit: "participant-min" },
  { id: "mcp_calls", label: "Connector calls", unit: "calls" },
  { id: "digests", label: "Digests", unit: "digests" },
  { id: "projects", label: "Extra apps", unit: "apps" },
];

export function meterOf(id: string) {
  return METERS.find((m) => m.id === id) ?? { id: id as Meter, label: id.replaceAll("_", " "), unit: "" };
}

/** The meter's label mid-sentence: "stored sessions", "AI credits", "Live Assist minutes". */
export function meterNoun(id: string): string {
  const label = meterOf(id).label;
  return /^(AI|Live Assist)\b/.test(label) ? label : label.charAt(0).toLowerCase() + label.slice(1);
}

/** "+5,000 sessions", "−$25.00". */
export function fmtUnits(meter: string, units: number, signed = true): string {
  const m = meterOf(meter);
  const abs = Math.abs(units);
  const body = m.usd ? fmtUsd(abs) : `${fmtNum(abs)}${m.unit ? ` ${m.unit}` : ""}`;
  if (!signed) return body;
  return `${units < 0 ? "−" : "+"}${body}`;
}

/** Every feature a plan can carry, in the words the customer sees. */
export const FEATURE_LABELS: Record<string, string> = {
  player: "Session player",
  signals: "Signals",
  heatmaps: "Heatmaps",
  slack: "Slack",
  funnels: "Funnels",
  integrations: "Integrations",
  mcp: "AI assistant connector",
  automatic_ai: "Automatic AI",
  live_assist: "Live Assist",
  sso: "Single sign-on",
  ip_allowlist: "Allowed networks",
  byok_ai: "Bring your own AI provider",
  everything: "Everything in Business",
  hipaa: "HIPAA",
  scim: "Directory sync",
  self_hosted: "Self-hosted option",
  canvas: "Canvas recording",
  webrtc: "Call diagnostics",
  webrtc_groups: "Multi-party calls",
};

export function featureLabel(id: string): string {
  return FEATURE_LABELS[id] ?? id.replaceAll("_", " ");
}

// ---------------------------------------------------------------------------
// Activity
// ---------------------------------------------------------------------------

/** Families of actions, as a filter. The value is the action prefix. */
export const ACTIVITY_FAMILIES: Array<{ value: string; label: string }> = [
  { value: "", label: "Everything" },
  { value: "staff.customer.", label: "Customer changes" },
  { value: "staff.credit.", label: "Credits" },
  { value: "support.access.", label: "Support access" },
  { value: "staff.member", label: "Platform team" },
  { value: "staff.system_issue.", label: "System issues" },
  { value: "staff.ai_feedback.", label: "AI feedback" },
  { value: "billing.", label: "Payments setup" },
  { value: "ai.", label: "AI setup" },
  { value: "mail.", label: "Email setup" },
];

const ACTION_LABELS: Record<string, string> = {
  "staff.credit.granted": "Granted credits",
  "staff.credit.reduced": "Reduced credits",
  "staff.credit.revoked": "Revoked a credit",
  "staff.quota_credit": "Granted credits",
  "staff.customer.updated": "Updated customer",
  "staff.customer.plan_changed": "Changed plan",
  "staff.customer.trial_granted": "Started a trial",
  "staff.trial": "Started a trial",
  "staff.customer.features_changed": "Changed feature grants",
  "staff.customer.member_role_changed": "Changed a member's role",
  "staff.customer.member_removed": "Removed a member",
  "staff.customer.member_invited": "Invited a member",
  "staff.customer.invitation_revoked": "Revoked an invitation",
  "support.access.requested": "Requested support access",
  "support.access.granted": "Support access granted",
  "support.access.denied": "Support access denied",
  "support.access.revoked": "Support access ended",
  "support.access.used": "Used support access",
  "staff.member_added": "Added to platform team",
  "staff.member_role_changed": "Changed platform role",
  "staff.member_removed": "Removed from platform team",
  "staff.invitation_cancelled": "Cancelled platform invitation",
  "staff.ai_feedback.acknowledged": "Acknowledged a rating",
  "staff.ai_feedback.reopened": "Reopened a rating",
  "staff.system_issue.acknowledged": "Acknowledged a system issue",
  "staff.system_issue.reopened": "Reopened a system issue",
  "staff.system_issue.investigation_requested": "Asked AI to investigate an issue",
  "billing.stripe.configured": "Saved payment settings",
  "billing.stripe.catalog_synced": "Synced the plan catalog",
  "mail.configured": "Saved email settings",
  "mail.test_sent": "Sent a test email",
  "ai.provider.create": "Added an AI provider",
  "ai.provider.upsert": "Updated an AI provider",
  "ai.provider.delete": "Removed an AI provider",
  "ai.provider.credentials_clear": "Cleared AI provider credentials",
  "ai.provider.verify": "Verified an AI provider",
  "ai.route.upsert": "Changed AI routing",
  "ai.route.phi_outside_baa": "Routed PHI outside a BAA",
  "ai.routing_mode.set": "Changed AI routing mode",
  "ai.catalog.model.save": "Saved an AI model",
  "ai.catalog.model.delete": "Removed an AI model",
};

export function actionLabel(action: string): string {
  if (ACTION_LABELS[action]) return ACTION_LABELS[action];
  const parts = action.split(".");
  const last = parts.at(-1) ?? action;
  return last.charAt(0).toUpperCase() + last.slice(1).replaceAll("_", " ");
}

export function actionTone(action: string): string {
  if (action.startsWith("staff.credit.granted") || action === "staff.quota_credit") return "ok";
  if (action.startsWith("staff.credit.")) return "warn";
  if (action.includes("removed") || action.includes("revoked") || action.includes("denied") || action.includes("delete")) return "danger";
  if (action.startsWith("support.access.")) return "info";
  return "neutral";
}

function str(v: unknown): string {
  return v == null ? "" : typeof v === "string" ? v : typeof v === "number" ? String(v) : "";
}

/** One line of detail for an entry, from what the action recorded. */
export function activityDetail(a: StaffActivity): string {
  const m = a.meta ?? {};
  switch (a.action) {
    case "staff.credit.granted":
    case "staff.credit.reduced":
    case "staff.credit.revoked":
    case "staff.quota_credit": {
      const units = Number(m.units ?? 0);
      const parts = [`${meterOf(str(m.meter)).label} ${fmtUnits(str(m.meter), units)}`];
      if (m.expiresAt) parts.push(`until ${formatDateTime(str(m.expiresAt))}`);
      if (m.note) parts.push(`“${str(m.note)}”`);
      return parts.join(" · ");
    }
    case "staff.customer.plan_changed": {
      const parts = [`${planLabel(str(m.from))} → ${planLabel(str(m.to))}`];
      if (m.mode === "trial") parts.push(`trial${m.days ? `, ${str(m.days)} days` : ""}`);
      if (m.note) parts.push(`“${str(m.note)}”`);
      return parts.join(" · ");
    }
    case "staff.customer.trial_granted":
    case "staff.trial":
      return `${planLabel(str(m.plan))}${m.days ? ` · ${str(m.days)} days` : ""}`;
    case "staff.customer.features_changed": {
      const from = new Set(Array.isArray(m.from) ? (m.from as string[]) : []);
      const to = Array.isArray(m.to) ? (m.to as string[]) : [];
      const added = to.filter((f) => !from.has(f)).map(featureLabel);
      const removed = [...from].filter((f) => !to.includes(f)).map(featureLabel);
      const parts: string[] = [];
      if (added.length) parts.push(`added ${added.join(", ")}`);
      if (removed.length) parts.push(`removed ${removed.join(", ")}`);
      if (m.note) parts.push(`“${str(m.note)}”`);
      return parts.join(" · ") || "no change";
    }
    case "staff.customer.updated": {
      const parts: string[] = [];
      const name = m.name as { from?: string; to?: string } | undefined;
      if (name?.to) parts.push(`renamed “${name.from ?? ""}” to “${name.to}”`);
      if (Array.isArray(m.settings) && m.settings.length) parts.push(`settings: ${(m.settings as string[]).join(", ")}`);
      if (m.profileChanged) parts.push("contact details");
      return parts.join(" · ");
    }
    case "staff.customer.member_role_changed":
    case "staff.member_role_changed":
      return [str(m.email), m.from ? `${roleWord(str(m.from))} → ${roleWord(str(m.to ?? m.role))}` : roleWord(str(m.role ?? m.to))].filter(Boolean).join(" · ");
    case "staff.customer.member_removed":
    case "staff.member_removed":
      return [str(m.email), roleWord(str(m.role))].filter(Boolean).join(" · ");
    case "staff.customer.member_invited":
    case "staff.member_added":
      return [str(m.email), roleWord(str(m.role))].filter(Boolean).join(" · ");
    case "support.access.requested": {
      const parts = [str(m.reason)];
      if (m.hours) parts.push(`${str(m.hours)} hours`);
      if (m.phiUnmask) parts.push("unmasked");
      if (Array.isArray(m.scopes) && m.scopes.length) parts.push((m.scopes as string[]).join(", "));
      return parts.filter(Boolean).join(" · ");
    }
    case "staff.ai_feedback.acknowledged":
    case "staff.ai_feedback.reopened":
      return [str(m.org), str(m.kind), str(m.rating)].filter(Boolean).join(" · ");
    case "staff.system_issue.acknowledged":
      return [str(m.source), m.note ? `“${str(m.note)}”` : ""].filter(Boolean).join(" · ");
    case "billing.stripe.catalog_synced":
      return `${str(m.products)} products · ${str(m.prices)} prices · ${str(m.meters)} meters`;
    case "mail.configured":
    case "mail.test_sent":
      return [str(m.provider), str(m.from ?? m.to)].filter(Boolean).join(" · ");
    default: {
      const keys = Object.keys(m).filter((k) => typeof m[k] === "string" || typeof m[k] === "number");
      return keys
        .slice(0, 3)
        .map((k) => `${k}: ${str(m[k])}`)
        .join(" · ");
    }
  }
}

/** "@example.com" rendering for who did it. */
export function actorName(a: StaffActivity): string {
  return a.actorEmail || "—";
}

export function ActivityFeed({
  filters,
  showCustomer = true,
  compact = false,
  limit,
  emptyTitle = "No activity yet",
  emptyHint = "Actions by the platform team appear here as they happen.",
}: {
  filters: ActivityFilters;
  showCustomer?: boolean;
  /** One line per entry and no load-more: for a recent-activity card. */
  compact?: boolean;
  limit?: number;
  emptyTitle?: ReactNode;
  emptyHint?: ReactNode;
}) {
  const q = useInfiniteQuery({
    queryKey: [...staffKeys.activity(filters), limit ?? 0],
    queryFn: ({ pageParam }) => staffApi.activity(filters, pageParam as string | undefined, limit ?? 50),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (compact ? undefined : last.nextCursor),
  });
  const rows = q.data?.pages.flatMap((p) => p.items) ?? [];

  if (q.isError) return <ErrorState error={q.error} retry={() => void q.refetch()} />;
  if (q.isPending) {
    return (
      <ul className={`activity-feed${compact ? " compact" : ""}`} aria-busy="true">
        {Array.from({ length: compact ? 3 : 4 }).map((_, i) => (
          <li key={i} className="activity-row">
            <span className="skeleton" style={{ width: 120 }}>loading</span>
            <span className="skeleton" style={{ width: `${45 + ((i * 17) % 40)}%` }}>loading</span>
          </li>
        ))}
      </ul>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="empty activity-empty">
        <h3>{emptyTitle}</h3>
        <p>{emptyHint}</p>
      </div>
    );
  }
  return (
    <>
      <ul className={`activity-feed${compact ? " compact" : ""}`}>
        {rows.map((a) => (
          <li key={a.id} className="activity-row">
            <div className="activity-when" title={formatDateTime(a.at)}>
              {formatRelative(a.at)}
            </div>
            <div className="activity-main">
              <div className="activity-head">
                <span className={`badge ${actionTone(a.action)}`}>{actionLabel(a.action)}</span>
                {showCustomer && a.orgId && (
                  <Link to="/staff/orgs/$org" params={{ org: a.orgId }} className="activity-org">
                    {a.orgName || a.orgId}
                  </Link>
                )}
                {showCustomer && !a.orgId && <span className="muted small">Platform</span>}
              </div>
              {activityDetail(a) && <div className="activity-detail">{activityDetail(a)}</div>}
              <div className="activity-meta">
                <span>{actorName(a)}</span>
                {!compact && a.ip && <span className="mono">{a.ip}</span>}
                {!compact && a.targetType && a.targetId && (
                  <span className="mono" title={`${a.targetType} ${a.targetId}`}>
                    {a.targetType} {a.targetId.length > 14 ? `${a.targetId.slice(0, 14)}…` : a.targetId}
                  </span>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
      {!compact && (
        <AutoLoadMore hasMore={!!q.hasNextPage} loading={q.isFetchingNextPage} loadMore={() => void q.fetchNextPage()} label="Loading more activity" />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

/** A value that follows `value` after `ms` of quiet. */
/**
 * A customer filter for platform-wide lists. Loads the roster once and lets the
 * select's own search narrow it; the first option means every customer.
 */
export function CustomerSelect({ value, onChange, allLabel = "All customers", ariaLabel = "Customer" }: { value: string; onChange: (v: string) => void; allLabel?: string; ariaLabel?: string }) {
  const orgs = useQuery({ queryKey: [...staffKeys.orgs({}), "picker"], queryFn: () => staffApi.orgs({}, undefined, 200), staleTime: 60_000 });
  const options = [{ value: "", label: allLabel }, ...(orgs.data?.items ?? []).map((o) => ({ value: o.id, label: o.name, detail: o.slug }))];
  return <SearchSelect value={value} options={options} onChange={onChange} aria-label={ariaLabel} searchPlaceholder="Search customers…" empty="No customer matches." />;
}

export function useDebounced<T>(value: T, ms = 250): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** "Jan 5, 2026". */
export function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}
